import axios from "axios";
import { useState } from "react";
import toastr from "toastr";

const normalizedId = (value) => (value === null || value === undefined ? null : String(value));

const useAppointmentRequestActions = ({
  data,
  appointmentRequestId,
  recurrenceScope,
  occurrenceStartAt,
  isManagerOrAdmin,
  selectedManagerPeer
}) => {
  const [pendingAction, setPendingAction] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const recurrenceRequestParams = () =>
    recurrenceScope
      ? { recurrence_scope: recurrenceScope, occurrence_start_at: occurrenceStartAt }
      : {};

  const openActionDialog = (action) => {
    setReason("");
    setReasonError("");
    setPendingAction(action);
  };

  const closeActionDialog = () => {
    if (isSubmitting) return;
    setPendingAction(null);
    setReason("");
    setReasonError("");
  };

  const redirectToUpdatedAppointment = (responseAppointment, expectedStatus) => {
    const updatedAppointment = responseAppointment || data;
    const url = new URL(window.location.href);
    const dateMatch = String(updatedAppointment.date || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      url.searchParams.set("year", String(parseInt(dateMatch[1], 10)));
      url.searchParams.set("month", String(parseInt(dateMatch[2], 10)));
    }

    const updatedStatus =
      updatedAppointment.effective_status || updatedAppointment.status || expectedStatus;
    const statusFilter = url.searchParams.get("status");
    if (statusFilter && statusFilter !== "all" && statusFilter !== updatedStatus)
      url.searchParams.delete("status");

    const peerFilter = url.searchParams.get("what_to_show");
    const updatedPeerId = normalizedId(
      updatedAppointment.effective_user_id === undefined
        ? updatedAppointment.user_id
        : updatedAppointment.effective_user_id
    );
    if (updatedStatus === "matched" && peerFilter === "unmatched") {
      url.searchParams.delete("what_to_show");
    } else if (
      updatedStatus === "matched" &&
      updatedPeerId &&
      peerFilter &&
      !["all", updatedPeerId].includes(peerFilter)
    ) {
      url.searchParams.set("what_to_show", updatedPeerId);
    } else if (
      updatedStatus === "unmatched" &&
      peerFilter &&
      !["all", "unmatched"].includes(peerFilter)
    ) {
      url.searchParams.delete("what_to_show");
    }

    url.searchParams.set(
      "highlight_appointment_id",
      updatedAppointment.occurrence_key || updatedAppointment.id || appointmentRequestId
    );
    url.hash = "peer-dashboard-calendar";
    const destination = url.toString();
    if (destination === window.location.href) window.location.reload();
    else window.location.assign(destination);
  };

  const updateMatchState = async (isMatching) => {
    const requestBody = {
      appointment_request_id: appointmentRequestId,
      ...recurrenceRequestParams()
    };
    if (isManagerOrAdmin && isMatching) requestBody.peer_id = selectedManagerPeer.id;

    const response = await axios.post(
      `/${isMatching ? "match_appointment_to_peer" : "unmatch_appointment_request"}.json`,
      requestBody
    );
    if (response.status !== 200)
      throw new Error("Unable to update appointment. Please refresh and try again.");

    toastr.success(
      isMatching
        ? "Matched appointment successfully, starting refresh to load new unmatched appointments"
        : "Unmatched appointment successfully, refreshing appointments"
    );
    redirectToUpdatedAppointment(
      response.data.appointment_request,
      isMatching ? "matched" : "unmatched"
    );
  };

  const denyAppointmentRequest = async (denialReason) => {
    const response = await axios.post("/deny_appointment_request.json", {
      appointment_request_id: appointmentRequestId,
      reason: denialReason,
      ...recurrenceRequestParams()
    });
    if (response.status !== 200)
      throw new Error("Failed to deny request. Please try again or reach out to admin.");
    toastr.success(
      "Denied appointment successfully, starting refresh to load new appointments state"
    );
    redirectToUpdatedAppointment(response.data.appointment_request, "denied");
  };

  const markPeerUnavailable = async (unavailableReason) => {
    const response = await axios.post("/mark_peer_unavailable.json", {
      appointment_request_id: appointmentRequestId,
      reason: unavailableReason,
      ...recurrenceRequestParams()
    });
    if (response.status !== 200)
      throw new Error("Failed to mark unavailable. Please try again or reach out to admin.");
    toastr.success("Marked unavailable successfully, refreshing appointments");
    redirectToUpdatedAppointment(response.data.appointment_request, "unmatched");
  };

  const confirmAppointmentUpdate = async () => {
    const response = await axios.post("/confirm_update.json", {
      appointment_request_id: appointmentRequestId,
      ...recurrenceRequestParams()
    });
    if (response.status !== 200)
      throw new Error("Failed to confirm the update. Please refresh and try again.");
    toastr.success("Appointment update confirmed, refreshing appointments");
    redirectToUpdatedAppointment(response.data.appointment_request, "matched");
  };

  const deleteAppointmentRequest = async () => {
    const response = await axios.post(
      `/appointment_request_delete/${appointmentRequestId}.json`,
      recurrenceRequestParams()
    );
    if (!(response.status > 199 && response.status < 300)) throw response.data;
    window.location.reload();
  };

  const confirmPendingAction = async () => {
    const trimmedReason = reason.trim();
    if (["deny", "unavailable"].includes(pendingAction) && !trimmedReason) {
      setReasonError("Enter a reason before continuing.");
      return;
    }

    setIsSubmitting(true);
    try {
      if (pendingAction === "match") await updateMatchState(true);
      if (pendingAction === "unmatch") await updateMatchState(false);
      if (pendingAction === "deny") await denyAppointmentRequest(trimmedReason);
      if (pendingAction === "unavailable") await markPeerUnavailable(trimmedReason);
      if (pendingAction === "confirm_update") await confirmAppointmentUpdate();
      if (pendingAction === "delete") await deleteAppointmentRequest();
    } catch (error) {
      const fallbackMessages = {
        match: "Unable to update appointment. Please refresh and try again.",
        unmatch: "Unable to update appointment. Please refresh and try again.",
        deny: "Failed to deny request. Please try again or reach out to admin.",
        unavailable: "Failed to mark unavailable. Please try again or reach out to admin.",
        confirm_update: "Failed to confirm the update. Please refresh and try again.",
        delete: "Failed to delete the appointment. Please refresh and try again."
      };
      toastr.error(
        error?.response?.data?.message || error.message || fallbackMessages[pendingAction]
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToEditAppointmentRequestPage = () => {
    const editUrl = new URL(
      `/appointment_request_edit/${appointmentRequestId}`,
      window.location.origin
    );
    Object.entries(recurrenceRequestParams()).forEach(([key, value]) =>
      editUrl.searchParams.set(key, value)
    );
    window.location.href = editUrl.toString();
  };

  return {
    closeActionDialog,
    confirmPendingAction,
    goToEditAppointmentRequestPage,
    isSubmitting,
    openActionDialog,
    pendingAction,
    reason,
    reasonError,
    setReason,
    setReasonError
  };
};

export default useAppointmentRequestActions;
